/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAlert from "components/MDAlert";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";
import Table from "@mui/material/Table";
import DataTable from "../../../examples/Tables/DataTable";
import data from "../../dashboard/components/Projects/data";
import useApi from "../../../components/Utils/api";
import MDInput from "../../../components/MDInput";
import toast from "react-hot-toast";
import subDays from "date-fns/subDays";
import startOfMonth from "date-fns/startOfMonth";
import addMonths from "date-fns/addMonths";
import endOfMonth from "date-fns/endOfMonth";
import { DateRangePicker } from "rsuite";
import { afterToday } from "rsuite/cjs/DateRangePicker/disabledDateUtils";
import reportsLineChartData from "../../dashboard/data/reportsLineChartData";

function TrackSms({ embedded = false, tabsSlot = null } = {}) {
  const { smsTablecolumns } = data();
  const { predefinedRanges } = reportsLineChartData;
  const [sms, setSms] = useState([]);
  const [filteredSms, setFilteredSms] = useState([]);

  // Filter state
  const [filterMobileNo, setFilterMobileNo] = useState("");
  const [filterUserId, setFilterUserId] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [lastMonthDateRange, setLastMonthDateRange] = useState([new Date().toISOString().slice(0, 10),  new Date().toISOString().slice(0, 10)]);
  // const [fromDate, setFromDate]= useState(lastMonthDateRange[0]);
  // const [toDate, setToDate ]= useState(lastMonthDateRange[1]);
  const api = useApi();
  const formatDate = (date) => {
    const isoDate = new Date(date).toISOString();
    return isoDate.slice(0, 10); // Extracts only the date part
  };
  const getSms = (lastMonthDateRange) => {
    if(lastMonthDateRange == null) return;
    setLastMonthDateRange(lastMonthDateRange);
    const data = {
      fromDate : formatDate(lastMonthDateRange[0]),
      toDate : formatDate(lastMonthDateRange[1]),
    }
    api.post("track/sms", data)
      .then((response) => {
        if(response.data.statusCodeValue === 200) {
          setSms(response.data.body);
        } else {
          setSms([]);
        }
      })
      .catch((error) => {
        if (error.response && error.response.status === 403) {
          toast.error("You do not have permission to perform this action.", { id: "PermissionError" });
          return;
        }
        // No matching records is surfaced by the table's own empty state, not a toast
        setSms([]);
      })
  }

  useEffect(() => {
    if(lastMonthDateRange !== null){
      getSms(lastMonthDateRange);
    }
  },[]);

  useEffect(() => {
    let result = [...sms];
    if (filterMobileNo.trim()) {
      result = result.filter(s => s.mobileNo?.includes(filterMobileNo.trim()));
    }
    if (filterUserId.trim()) {
      result = result.filter(s => s.id?.toLowerCase().includes(filterUserId.trim().toLowerCase()));
    }
    if (filterStatus !== "") {
      result = result.filter(s => String(s.status) === filterStatus);
    }
    setFilteredSms(result);
  }, [sms, filterMobileNo, filterUserId, filterStatus]);

  const resetFilters = () => {
    setFilterMobileNo("");
    setFilterUserId("");
    setFilterStatus("");
  };

  const content = (
      <MDBox
        mt={embedded ? 0 : 3}
        mb={embedded ? 0 : 3}
        sx={embedded ? {
          flex: "1 1 auto",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        } : {
          display: "flex",
          flexDirection: "column",
          // Fills the viewport down to the screen edge: 64px navbar + 24px
          // DashboardLayout bottom padding + this box's own 24px top/bottom margins.
          height: "calc(100vh - 136px)",
        }}
      >
          <Grid container alignItems="center" sx={{ flexShrink: 0, mb: 3 }}>
            <Grid item xs={4}>
              <MDBox>
                <MDTypography variant="h5" fontWeight="medium">
                  Track
                </MDTypography>
                <MDTypography variant="button" fontWeight="regular" color="secondary">
                  {filteredSms.length} Messages
                </MDTypography>
              </MDBox>
            </Grid>
            <Grid item xs={4} display="flex" justifyContent="center">
              <DateRangePicker
                  ranges={predefinedRanges}
                  onChange={(date) => getSms(date)}
                  format="d MMMM yyyy"
                  value={Object.keys(lastMonthDateRange).length !== 0 ? [new Date(lastMonthDateRange[0]), new Date(lastMonthDateRange[1])] : []}
              />
            </Grid>
            <Grid item xs={4} />
          </Grid>
          {tabsSlot && (
            <MDBox sx={{ flexShrink: 0, mb: 3 }}>
              {tabsSlot}
            </MDBox>
          )}
          <MDBox
            sx={(theme) => ({
              flex: "1 1 auto",
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              border: `1px solid ${theme.palette.grey[300]}`,
              borderRadius: "8px",
              background: theme.palette.white.main,
              overflow: "hidden",
            })}
          >
            {/* Filter Panel */}
            <MDBox px={2} py={1.5} sx={(theme) => ({ flexShrink: 0, borderBottom: `1px solid ${theme.palette.grey[300]}`, background: theme.palette.background.hover })}>
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={12} sm={6} md={3} lg={true}>
                  <TextField
                    label="Mobile No"
                    value={filterMobileNo}
                    onChange={(e) => setFilterMobileNo(e.target.value)}
                    fullWidth size="small" variant="outlined"
                    InputProps={{ style: { fontSize: "0.8rem" } }}
                    InputLabelProps={{ style: { fontSize: "0.8rem" } }}
                    sx={{ "& .MuiOutlinedInput-root": { height: "38px" } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={true}>
                  <TextField
                    label="User ID"
                    value={filterUserId}
                    onChange={(e) => setFilterUserId(e.target.value)}
                    fullWidth size="small" variant="outlined"
                    InputProps={{ style: { fontSize: "0.8rem" } }}
                    InputLabelProps={{ style: { fontSize: "0.8rem" } }}
                    sx={{ "& .MuiOutlinedInput-root": { height: "38px" } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={true}>
                  <TextField
                    select
                    label="Status"
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    fullWidth size="small" variant="outlined"
                    InputProps={{ style: { fontSize: "0.8rem" } }}
                    InputLabelProps={{ style: { fontSize: "0.8rem" } }}
                    sx={{
                      "& .MuiOutlinedInput-root": { height: "38px" },
                      "& .MuiSelect-select": { fontSize: "0.8rem" },
                    }}
                  >
                    <MenuItem value="" style={{ fontSize: "0.8rem" }}>All</MenuItem>
                    <MenuItem value="1" style={{ fontSize: "0.8rem" }}>Sent</MenuItem>
                    <MenuItem value="0" style={{ fontSize: "0.8rem" }}>Not Sent</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg="auto">
                  <MDButton variant="outlined" color="secondary" onClick={resetFilters} size="small" style={{ fontSize: "0.75rem", padding: "5px 16px", whiteSpace: "nowrap" }}>
                    Reset
                  </MDButton>
                </Grid>
              </Grid>
            </MDBox>
            <MDBox sx={{ flex: "1 1 auto", minHeight: 0 }}>
              <DataTable
                table={{ columns : smsTablecolumns, rows : filteredSms }}
                showTotalEntries={true}
                isSorted={true}
                noEndBorder
                canSearch={false}
                entriesPerPage={true}
                entriesPerPagePosition="bottom"
                bodyMaxHeight="100%"
              />
            </MDBox>
          </MDBox>
      </MDBox>
  );

  // embedded=true renders content only (used as a tab inside layouts/track)
  if (embedded) return content;

  return (
    <DashboardLayout>
      {content}
      <Footer />
    </DashboardLayout>
  );
}

TrackSms.propTypes = {
  // eslint-disable-next-line react/require-default-props
  embedded: PropTypes.bool,
  // eslint-disable-next-line react/require-default-props
  tabsSlot: PropTypes.node,
};

export default TrackSms;
